import { injectable } from 'inversify'
import { action, makeObservable, observable, runInAction } from 'mobx'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'

/**
 * How this viewer wants lists laid out, as opposed to what they want to see in
 * them — which is FiltersRepository's job.
 *
 * One preference for the whole app rather than one per list: pinning row actions
 * is a habit, not an opinion about workspaces in particular, and a viewer who
 * unpins on one narrow screen means it for every table they open on it. Should a
 * genuinely per-list preference arrive (which columns are shown, say), it keys by
 * scope here; the app-wide ones stay flat.
 *
 * A Repository rather than Presenter state (P-28) for the usual lifetime reason:
 * the toggle unmounts on every navigation and the preference has to outlive it.
 * User-scoped for the same reason FiltersRepository is — it belongs to whoever is
 * signed in, so the next login starts from the default rather than inheriting a
 * stranger's layout.
 */
@injectable()
export class ViewPreferencesRepository implements UserScopedState {
  /**
   * Whether row actions stay visible while a wide table is scrolled sideways.
   *
   * Pinned by default: the actions column is the one a row is opened *for*, and
   * a control that scrolls out of reach is a control the reader has to go
   * looking for. Unpinning is for the case the pin creates — on a narrow screen
   * the pinned column is permanently spending width the data could use.
   */
  actionsPinnedPm: boolean = true

  constructor() {
    makeObservable(this, {
      actionsPinnedPm: observable,
      toggleActionsPinned: action,
    })
  }

  toggleActionsPinned = (): void => {
    this.actionsPinnedPm = !this.actionsPinnedPm
  }

  resetUserScopedState = (): void => {
    runInAction(() => {
      this.actionsPinnedPm = true
    })
  }
}
